import { trackEvent } from '@services/tracking';
import { AuthorId, Post } from '../model/post.types';
import { Reply } from '../model/reply.types';
import { CommunityId } from '../model/community.types';
import { FeedAuthorFilter } from '../model/feedFilter.types';

// Single owner for communities-v2 Mixpanel events. trackEvent auto-injects
// Influencer ID/Name from auth context, so payloads never carry them.

export type UserBlockedSource = 'feed' | 'moderation';

type AuthorType = 'Creator' | 'Consumer';

const authorType = (isCreator: boolean): AuthorType =>
  isCreator ? 'Creator' : 'Consumer';

const postMediaType = (media: Post['media']): string =>
  media.map((item) => item.type).join(', ');

const fileLabel = (file: File): string => {
  if (file.type.startsWith('video')) return 'video';
  if (file.type.startsWith('audio')) return 'audio';
  return 'image';
};

const fileMediaType = (media: File[], videoCount: number): string =>
  [
    ...media.map(fileLabel),
    ...Array.from({ length: videoCount }, () => 'video'),
  ].join(', ');

// Cross-platform value set for Community Filter Applied ('Coach only' on
// mobile); web has no coach-only segment so it never sends that value.
const filterLabel: Record<FeedAuthorFilter, 'Mine' | 'Everyone'> = {
  mine: 'Mine',
  all: 'Everyone',
};

export const analytics = {
  trackCommunityViewed: (posts: Post[]): void =>
    trackEvent({
      type: 'Community Viewed',
      payload: {
        'Includes a creator post': String(
          posts.some((post) => post.author.isCreator),
        ),
        'Includes a consumer post': String(
          posts.some((post) => !post.author.isCreator),
        ),
      },
    }),

  trackPostCreated: (media: File[], videoCount: number): void =>
    trackEvent({
      type: 'Post Created',
      payload: {
        'Media Count': media.length + videoCount,
        'Media Type': fileMediaType(media, videoCount),
      },
    }),

  trackPostFailed: (media: File[], videoCount: number, reason: string): void =>
    trackEvent({
      type: 'Post Failed',
      payload: {
        'Media Count': media.length + videoCount,
        'Media Type': fileMediaType(media, videoCount),
        Reason: reason,
      },
    }),

  trackBroadcastSent: (): void =>
    trackEvent({ type: 'Broadcast message sent' }),

  trackCommentAdded: (post: Post): void =>
    trackEvent({
      type: 'Comment Added',
      payload: {
        'Post Type': authorType(post.author.isCreator),
        'Media Type': postMediaType(post.media),
      },
    }),

  trackReplyAdded: (parentCommentAuthorId: AuthorId): void =>
    trackEvent({
      type: 'Reply Added',
      payload: { 'Parent Comment Author ID': String(parentCommentAuthorId) },
    }),

  trackCommentsExpanded: (trigger: 'Comment Count' | 'Add Comment'): void =>
    trackEvent({ type: 'Comments Expanded', payload: { Trigger: trigger } }),

  trackViewPreviousComments: (): void =>
    trackEvent({ type: 'View Previous Comments Tapped' }),

  trackReactionAdded: (post: Post): void =>
    trackEvent({
      type: 'Reaction Added',
      payload: {
        'Post Type': authorType(post.author.isCreator),
        'Reaction Location': 'Post',
        'Media Type': postMediaType(post.media),
      },
    }),

  trackReplyReactionAdded: (reply: Reply): void =>
    trackEvent({
      type: 'Reaction Added',
      payload: {
        'Post Type': authorType(reply.author.isCreator),
        'Reaction Location': 'Reply',
        'Media Type': 'text',
      },
    }),

  trackReactionRemoved: (communityId: CommunityId, post: Post): void =>
    trackEvent({
      type: 'Reaction Removed',
      payload: {
        'Community ID': String(communityId),
        'Reaction Location': 'Post',
        'Post Type': authorType(post.author.isCreator),
      },
    }),

  trackReplyReactionRemoved: (communityId: CommunityId, reply: Reply): void =>
    trackEvent({
      type: 'Reaction Removed',
      payload: {
        'Community ID': String(communityId),
        'Reaction Location': 'Reply',
        'Post Type': authorType(reply.author.isCreator),
      },
    }),

  trackPostDeleted: (post: Post): void =>
    trackEvent({
      type: 'Post Deleted',
      payload: {
        'Post Owner': authorType(post.author.isCreator),
        'Media Type': postMediaType(post.media),
      },
    }),

  trackCommentDeleted: (reply: Reply): void =>
    trackEvent({
      type: 'Comment Deleted',
      payload: {
        'Comment Owner': authorType(reply.author.isCreator),
        'Media Type': 'text',
      },
    }),

  trackSearchApplied: (communityId: CommunityId, term: string): void =>
    trackEvent({
      type: 'Community Search Applied',
      payload: { Term: term, 'Community ID': String(communityId) },
    }),

  trackFilterApplied: (
    communityId: CommunityId,
    author: FeedAuthorFilter,
  ): void =>
    trackEvent({
      type: 'Community Filter Applied',
      payload: {
        Filter: filterLabel[author],
        'Community ID': String(communityId),
      },
    }),

  trackReportedPostsVisited: (): void =>
    trackEvent({ type: 'Reported Posts Visited' }),

  trackBlockedUsersVisited: (): void =>
    trackEvent({ type: 'Blocked Users Visited' }),

  trackUserBlocked: (source: UserBlockedSource): void =>
    trackEvent({ type: 'User Blocked', payload: { Source: source } }),

  trackUserUnblocked: (): void => trackEvent({ type: 'User Unblocked' }),

  trackReportedPostKept: (): void => trackEvent({ type: 'Reported Post Kept' }),

  trackReportedPostRemoved: (): void =>
    trackEvent({ type: 'Reported Post Removed' }),

  trackAuthorEmailCopied: (): void =>
    trackEvent({ type: 'Author Email Copied' }),

  trackRepliedViaDm: (): void => trackEvent({ type: 'Replied Via Dm' }),

  trackReplyThreadExpanded: (): void =>
    trackEvent({ type: 'Reply Thread Expanded' }),

  trackMoreFeedLoaded: (): void => trackEvent({ type: 'More Feed Loaded' }),

  trackNewPostsIndicatorClicked: (): void =>
    trackEvent({ type: 'New Posts Indicator Clicked' }),
};
